import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { hasRole } from '@fkwd-qc/shared';
import type { Role, SessionUser } from '@fkwd-qc/shared';
import { ApiRequestError, getSession, login, logout, onUnauthorized, setPassword } from '@/lib/api';
import { SESSION_QUERY_KEY } from '@/lib/query-keys';

export { SESSION_QUERY_KEY } from '@/lib/query-keys';

const SESSION_STALE_TIME_MS = 60_000;
const HTTP_UNAUTHORIZED = 401;

/**
 * What the session context hands to consumers.
 *
 * @since 1.0.0
 */
export interface SessionContextValue {
    user: SessionUser | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    signIn: (email: string, password: string) => Promise<void>;
    setPasswordAndSignIn: (token: string, password: string) => Promise<void>;
    signOut: () => Promise<void>;
    can: (required: Role) => boolean;
}

const SessionContext = createContext<SessionContextValue | null>(null);

/**
 * Props for the session provider.
 *
 * @since 1.0.0
 */
export interface SessionProviderProps {
    children: ReactNode;
}

/**
 * Reads the session, treating a 401 as "signed out" rather than as an error.
 *
 * @since 1.0.0
 * @returns the signed-in user, or null
 */
async function fetchSession(): Promise<SessionUser | null> {
    try {
        return await getSession();
    } catch (error) {
        if (error instanceof ApiRequestError && error.status === HTTP_UNAUTHORIZED) {
            return null;
        }

        throw error;
    }
}

/**
 * Holds the signed-in user for the whole app and keeps it in step with the API, including
 * when any other request comes back unauthorized.
 *
 * @since 1.0.0
 * @param props - the subtree to provide the session to
 * @returns the provider element
 */
export function SessionProvider({ children }: SessionProviderProps) {
    const queryClient = useQueryClient();

    const sessionQuery = useQuery({
        queryKey: SESSION_QUERY_KEY,
        queryFn: fetchSession,
        retry: false,
        throwOnError: false,
        staleTime: SESSION_STALE_TIME_MS,
    });

    useEffect(
        () =>
            onUnauthorized(() => {
                queryClient.setQueryData<SessionUser | null>(SESSION_QUERY_KEY, null);
            }),
        [queryClient],
    );

    const adoptUser = useCallback(
        async (user: SessionUser): Promise<void> => {
            queryClient.setQueryData<SessionUser | null>(SESSION_QUERY_KEY, user);
            await queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] !== SESSION_QUERY_KEY[0] });
        },
        [queryClient],
    );

    const signIn = useCallback(
        async (email: string, password: string): Promise<void> => {
            await adoptUser(await login({ email, password }));
        },
        [adoptUser],
    );

    const setPasswordAndSignIn = useCallback(
        async (token: string, password: string): Promise<void> => {
            await adoptUser(await setPassword({ token, password }));
        },
        [adoptUser],
    );

    const signOut = useCallback(async (): Promise<void> => {
        try {
            await logout();
        } finally {
            queryClient.setQueryData<SessionUser | null>(SESSION_QUERY_KEY, null);
            queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== SESSION_QUERY_KEY[0] });
        }
    }, [queryClient]);

    const user = sessionQuery.data ?? null;

    const value = useMemo<SessionContextValue>(
        () => ({
            user,
            isLoading: sessionQuery.isPending,
            isAuthenticated: user !== null,
            signIn,
            setPasswordAndSignIn,
            signOut,
            can: (required: Role) => (user === null ? false : hasRole(user.role, required)),
        }),
        [user, sessionQuery.isPending, signIn, setPasswordAndSignIn, signOut],
    );

    return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/**
 * Reads the session context.
 *
 * @since 1.0.0
 * @returns the current session value
 */
export function useSession(): SessionContextValue {
    const context = useContext(SessionContext);

    if (context === null) {
        throw new Error('useSession must be used inside a SessionProvider (1E)');
    }

    return context;
}
